import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import {
    getCheckoutFlow,
    getCheckoutPath,
    getValidatedCheckoutReturnTo,
    normalizeCheckoutLocale,
} from '@/checkout-paths'
import {
    CheckoutSuccessFallbackStatusContent,
    CheckoutSuccessRichContent,
} from '@/components/checkout-success/page/content'
import {
    CheckoutSuccessLoadingState,
    CheckoutSuccessStateCard,
} from '@/components/checkout-success/page/states'
import {
    getFallbackCheckoutSuccessAction,
    shouldUseRichCheckoutSuccessLayout,
} from '@/components/checkout-success/page/view-model'
import {
    useGetCheckoutPaymentStatus,
    useGetCheckoutSuccessPageData,
} from '@/hooks/api/use-reservation-checkout'
import { useCheckoutTracking } from '@/hooks/use-checkout-tracking'
import { getStoredReservationToken } from '@/session-storage'
import { getWebAppConfigValue } from '@/web-app-root'

export function scrollCheckoutSuccessToTop(): void {
    window.scrollTo({ top: 0, left: 0 })
}

export default function CheckoutSuccessView({
    isPwa = false,
}: {
    isPwa?: boolean,
}) {
    const [searchParams] = useSearchParams()
    const { t, i18n } = useTranslation()

    const paymentId = searchParams.get('payment_id')
    const reservationToken = getStoredReservationToken()
    const locale = normalizeCheckoutLocale(i18n.resolvedLanguage || i18n.language)
    const checkoutFlow = getCheckoutFlow(searchParams)
    const returnTo = getValidatedCheckoutReturnTo(searchParams)
    const isLoggedIn = Boolean(getWebAppConfigValue('user-id'))
    const checkoutSearchParams = new URLSearchParams(searchParams)
    checkoutSearchParams.delete('payment_id')
    const checkoutPath = getCheckoutPath({
        isPwa,
        locale,
        searchParams: checkoutSearchParams,
    })

    useEffect(scrollCheckoutSuccessToTop, [])

    const paymentStatusQuery = useGetCheckoutPaymentStatus(paymentId, {
        enabled: !!paymentId && !!reservationToken,
    })

    const useRichLayout = paymentStatusQuery.status === 'success'
        && shouldUseRichCheckoutSuccessLayout(paymentStatusQuery.data.status)

    const successPageDataQuery = useGetCheckoutSuccessPageData(paymentId, {
        enabled: useRichLayout && !!paymentId && !!reservationToken,
    })

    useCheckoutTracking({
        isPwa,
        checkoutFlow,
        paymentStatus: paymentStatusQuery.status === 'success'
            ? paymentStatusQuery.data.status
            : null,
        purchaseTracking: successPageDataQuery.status === 'success'
            ? successPageDataQuery.data.purchase_tracking
            : null,
    })

    const fallbackAction = getFallbackCheckoutSuccessAction({
        isPwa,
        isLoggedIn,
        locale,
        checkoutPath,
        checkoutFlow,
        returnTo,
        t,
    })

    if (!paymentId) {
        return (
            <CheckoutSuccessStateCard
                variant="failed"
                title={t('success.failed.title')}
                text={t('checkout.paymentResult.missingPaymentId')}
                actionHref={fallbackAction.actionHref}
                actionLabel={fallbackAction.actionLabel}
            />
        )
    }

    if (!reservationToken) {
        return (
            <CheckoutSuccessStateCard
                variant="failed"
                title={t('success.failed.title')}
                text={t('checkout.paymentResult.missingReservationToken')}
                actionHref={fallbackAction.actionHref}
                actionLabel={fallbackAction.actionLabel}
            />
        )
    }

    if (paymentStatusQuery.status === 'pending') {
        return <CheckoutSuccessLoadingState />
    }

    if (paymentStatusQuery.status === 'error') {
        return (
            <CheckoutSuccessStateCard
                variant="failed"
                title={t('success.failed.title')}
                text={paymentStatusQuery.error.message}
                actionHref={fallbackAction.actionHref}
                actionLabel={fallbackAction.actionLabel}
            />
        )
    }

    if (useRichLayout) {
        return (
            <CheckoutSuccessRichContent
                paymentStatus={paymentStatusQuery.data.status}
                successPageDataQuery={successPageDataQuery}
                isPwa={isPwa}
                isLoggedIn={isLoggedIn}
                locale={locale}
                checkoutPath={checkoutPath}
                checkoutFlow={checkoutFlow}
                returnTo={returnTo}
            />
        )
    }

    return (
        <CheckoutSuccessFallbackStatusContent
            paymentStatus={paymentStatusQuery.data.status}
            isPwa={isPwa}
            isLoggedIn={isLoggedIn}
            locale={locale}
            checkoutPath={checkoutPath}
            checkoutFlow={checkoutFlow}
            returnTo={returnTo}
        />
    )
}
